import React, { useMemo, useState } from 'react';
import { Container, Card, CardBody, Button } from 'reactstrap';

import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { buildExportHtmlDocument } from '../../../components/Utils/export';

const SAMPLE_COLUMNS = [
  { field: 'documento', header: 'Documento', Export: true },
  { field: 'status', header: 'Status', Export: true },
  { field: 'cliente', header: 'Cliente', Export: true },
  { field: 'valor', header: 'Valor (R$)', align: 'right', Export: true },
  { field: 'data', header: 'Data', Export: true },
];

const SAMPLE_DATA = [
  { documento: 'DOC-0001', status: 'Aprovado', cliente: 'Estadão Logística', valor: 'R$ 1.250,00', data: '01/08/2026' },
  { documento: 'DOC-0002', status: 'Pendente', cliente: 'Grupo Alfa', valor: 'R$ 3.480,90', data: '03/08/2026' },
  { documento: 'DOC-0003', status: 'Reprovado', cliente: 'Bravo Transportes', valor: 'R$ 890,00', data: '10/08/2026' },
  { documento: 'DOC-0004', status: 'Aprovado', cliente: 'Delta Cargas', valor: 'R$ 12.340,00', data: '15/08/2026' },
  { documento: 'DOC-0005', status: 'Aprovado', cliente: 'Estadão Logística', valor: 'R$ 560,50', data: '20/08/2026' },
];

const SAMPLE_APPLIED_FILTERS = [
  {
    status: { field: 'Status', label: 'Aprovado', value: 'Aprovado' },
    periodo: { field: 'Período', label: '01/08/2026 a 20/08/2026', value: '01/08/2026 a 20/08/2026' },
  },
];

const SAMPLE_FLOW_DATA = [
  { name: 'Solicitação', description: 'Etapa concluída' },
  { name: 'Aprovação', description: 'Em análise' },
  // { name: 'Faturamento', description: 'Aguardando' },
];

const SAMPLE_CONTEXT_DATA = [
  { label: 'Código da Transação', value: 'PREVIEW-TEMPLATE', largura: 'col' },
  { label: 'Filial', value: 'Matriz - São Paulo', largura: 'col' },
  { label: 'Responsável', value: 'Jefferson Santos', largura: 'col' },
];

// Estrutura espelha o componente CardContextExpansive (data[0] = coluna esquerda, data[1] = coluna direita)
const SAMPLE_CONTEXT_COLAPSE_DATA = [
  [
    // { titulo: 'Código da Transação', valor: 'PREVIEW-TEMPLATE', largura: 'col-12' },
    // { titulo: 'Código da Transação', valor: 'PREVIEW-TEMPLATE', largura: 'col-6' },
    // { titulo: 'Código da Transação', valor: 'PREVIEW-TEMPLATE', largura: 'col-6' },
    { qrcode: 'PREVIEW-TEMPLATE', largura: 'col-12' }
  ],
  [
    {
      titulo: 'Observacao',
      subtitulo: '',
      descricao: 'Informações complementares exibidas ao expandir o card, incluindo observações e histórico resumido da transação.',
      largura: 'col-12',
    },
    // { titulo: 'Última Atualização', subtitulo: '25/08/2026 14:32', largura: 'col-6' },
    // { titulo: 'Origem', subtitulo: 'Integração ERP', largura: 'col-6' },
  ],
];

const ExportTemplatePreviewView = () => {
  document.title = 'Preview Template de Exportação | UI - Defender';

  const [refreshKey, setRefreshKey] = useState(0);

  const options = useMemo(() => ({
    title: 'Relatório de Dados (Preview)',
    transactionCode: 'PREVIEW-TEMPLATE',
    columns: SAMPLE_COLUMNS,
    filteredData: SAMPLE_DATA,
    appliedFilters: SAMPLE_APPLIED_FILTERS,
    flowData: SAMPLE_FLOW_DATA,
    contextData: SAMPLE_CONTEXT_DATA,
    contextColapseData: SAMPLE_CONTEXT_COLAPSE_DATA,
    enableContextColapseQRCode: true,
    tableTitle: "Detalhes da Entrega"
  }), []);

  const htmlDocument = useMemo(
    () => buildExportHtmlDocument(options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, refreshKey],
  );

  const handleOpenInNewTab = () => {
    const blob = new Blob([htmlDocument], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    const blob = new Blob([htmlDocument], { type: 'text/html;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'preview-template-exportacao.html');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <React.Fragment>
      <div>
        <Container fluid={true}>
          <Breadcrumbs
            items={[
              { chave: 'UI Lab', transacao: 'ui' },
              { chave: 'Preview Template de Exportação', transacao: 'export-template-preview' },
            ]}
            transactionCode="export-template-preview"
            transactionDescribe="Preview Template de Exportação"
          />

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Preview do template de exportação (LayoutHtml.js)</h5>
                  <p className="text-muted mb-0">
                    Renderização do HTML gerado por <code>buildExportHtmlDocument</code> com dados de exemplo,
                    idêntico ao que é produzido nas exportações de HTML/PDF da aplicação.
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <Button color="secondary" outline onClick={() => setRefreshKey((k) => k + 1)}>
                    Atualizar
                  </Button>
                  <Button color="secondary" outline onClick={handleOpenInNewTab}>
                    Abrir em nova aba
                  </Button>
                  <Button color="primary" onClick={handleDownload}>
                    Baixar HTML
                  </Button>
                </div>
              </div>

              <iframe
                key={refreshKey}
                title="Preview do Template de Exportação"
                srcDoc={htmlDocument}
                style={{
                  width: '100%',
                  height: '80vh',
                  border: '1px solid #e0e0e0',
                  borderRadius: 4,
                  background: '#fff',
                }}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ExportTemplatePreviewView;
